/**
 * /payroll/year-end/1099-misc/:year — 1099-MISC batch with per-recipient box editor.
 *
 * Unlike NEC (which auto-aggregates from contractor paychecks), MISC needs
 * the user to enter box amounts per recipient. The form supports rents,
 * royalties, medical, attorney, etc.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Paper,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import { api, getToken } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/year-end_/1099-misc/$year")({
  component: Form1099MISCBatchPage,
});

interface Contractor {
  id: string; business_name: string | null; first_name: string | null;
  last_name: string | null; address_state: string | null; is_active: boolean;
}

const MISC_BOXES = [
  { key: "box1_rents", label: "Box 1 Rents" },
  { key: "box2_royalties", label: "Box 2 Royalties" },
  { key: "box3_other_income", label: "Box 3 Other income" },
  { key: "box4_federal_withheld", label: "Box 4 Federal tax withheld" },
  { key: "box5_fishing_boat", label: "Box 5 Fishing boat proceeds" },
  { key: "box6_medical_health", label: "Box 6 Medical & health" },
  { key: "box8_substitute_payments", label: "Box 8 Substitute payments" },
  { key: "box9_crop_insurance", label: "Box 9 Crop insurance" },
  { key: "box10_gross_attorney", label: "Box 10 Gross to attorney" },
  { key: "box11_fish_purchases", label: "Box 11 Fish purchases" },
  { key: "box12_section_409a", label: "Box 12 Section 409A" },
  { key: "box14_excess_golden_parachute", label: "Box 14 Excess golden parachute" },
  { key: "box15_nonqual_deferred", label: "Box 15 Nonqual deferred comp" },
] as const;

type RecipientBoxes = Record<string, number | boolean | string>;

function Form1099MISCBatchPage() {
  const { year } = Route.useParams();
  const yr = Number(year);
  const qc = useQueryClient();

  // State per recipient: { contractor_id: { box1_rents: 36000, box2_royalties: 0, ...,  box7_payer_made_direct_sales: false } }
  const [boxes, setBoxes] = useState<Record<string, RecipientBoxes>>({});
  const [editing, setEditing] = useState<Contractor | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<any | null>(null);

  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ["payroll", "contractors"],
    queryFn: async () => (await api(`/api/payroll/contractors`)) as Contractor[],
  });

  const transmit = useMutation({
    mutationFn: async () => {
      const recipients = Object.entries(boxes).map(([cid, b]) => ({
        contractor_id: cid,
        box1_rents: Number(b.box1_rents || 0),
        box2_royalties: Number(b.box2_royalties || 0),
        box3_other_income: Number(b.box3_other_income || 0),
        box4_federal_withheld: Number(b.box4_federal_withheld || 0),
        box5_fishing_boat: Number(b.box5_fishing_boat || 0),
        box6_medical_health: Number(b.box6_medical_health || 0),
        box7_payer_made_direct_sales: Boolean(b.box7_payer_made_direct_sales),
        box8_substitute_payments: Number(b.box8_substitute_payments || 0),
        box9_crop_insurance: Number(b.box9_crop_insurance || 0),
        box10_gross_attorney: Number(b.box10_gross_attorney || 0),
        box11_fish_purchases: Number(b.box11_fish_purchases || 0),
        box12_section_409a: Number(b.box12_section_409a || 0),
        box14_excess_golden_parachute: Number(b.box14_excess_golden_parachute || 0),
        box15_nonqual_deferred: Number(b.box15_nonqual_deferred || 0),
      }));
      return await api(`/api/payroll/year-end/1099-misc/${yr}/transmit`, {
        method: "POST", body: { recipients },
      });
    },
    onSuccess: (r) => {
      setResult(r); setConfirm(false);
      qc.invalidateQueries({ queryKey: ["payroll", "year-end"] });
    },
  });

  const recipientName = (c: Contractor) =>
    c.business_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.id;

  const downloadPdf = (cid: string, name: string, copy = "B") => {
    const body = boxes[cid] || {};
    const url = `/api/payroll/year-end/1099-misc/${yr}/${cid}.pdf?copy=${copy}`;
    fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
      body: JSON.stringify({ contractor_id: cid, ...body }),
    }).then((r) => r.blob()).then((blob) => {
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = `1099-MISC-${yr}-${name}.pdf`; a.click();
      URL.revokeObjectURL(u);
    });
  };

  const recipientsWithBoxes = Object.keys(boxes).filter((cid) => {
    const b = boxes[cid];
    return MISC_BOXES.some((bx) => Number(b[bx.key] || 0) > 0);
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton component={Link} to="/payroll/year-end" size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary">Back to Year-End</Typography>
      </Stack>
      <Typography variant="h4" fontWeight={700} mb={1}>
        1099-MISC Batch — Tax Year {yr}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Enter per-recipient amounts for rents, royalties, medical, attorney, and other non-NEC payments.
        Recipients with zero in every box are excluded.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <HelpHint docPath="payroll-year-end/filing-1099-misc.md" label="1099-MISC filing guide (with box reference)" />
      </Box>

      <Stack direction="row" spacing={2} mb={3}>
        <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setConfirm(true)}
                disabled={recipientsWithBoxes.length === 0}>
          Transmit {recipientsWithBoxes.length} 1099-MISCs to IRIS
        </Button>
      </Stack>

      {result && (
        <Alert severity={result.status === "rejected" ? "error" : result.status === "dry_run" ? "warning" : "success"} sx={{ mb: 3 }}>
          <strong>{result.status?.toUpperCase()}</strong> — submission <code>{result.submission_id}</code>, {result.record_count} records.
        </Alert>
      )}

      {isLoading ? <CircularProgress /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Recipient</TableCell>
                <TableCell>Box 1 Rents</TableCell>
                <TableCell>Box 6 Medical</TableCell>
                <TableCell>Box 10 Attorney</TableCell>
                <TableCell>Total filled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contractors.map((c) => {
                const b = boxes[c.id] || {};
                const filled = MISC_BOXES.filter((bx) => Number(b[bx.key] || 0) > 0).length;
                return (
                  <TableRow key={c.id}>
                    <TableCell><Typography variant="body2" fontWeight={500}>{recipientName(c)}</Typography></TableCell>
                    <TableCell>${Number(b.box1_rents || 0).toFixed(2)}</TableCell>
                    <TableCell>${Number(b.box6_medical_health || 0).toFixed(2)}</TableCell>
                    <TableCell>${Number(b.box10_gross_attorney || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={`${filled} boxes`} color={filled > 0 ? "primary" : "default"} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<EditIcon />} onClick={() => setEditing(c)}>Edit Boxes</Button>
                      <Button size="small" startIcon={<PictureAsPdfIcon />} disabled={filled === 0}
                              onClick={() => downloadPdf(c.id, recipientName(c), "B")}>
                        Copy B
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Box editor dialog */}
      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          1099-MISC Boxes — {editing && recipientName(editing)}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
            {MISC_BOXES.map((bx) => (
              <TextField key={bx.key} label={bx.label} type="number" size="small"
                         value={editing ? (boxes[editing.id]?.[bx.key] ?? 0) : 0}
                         onChange={(e) => editing && setBoxes((p) => ({
                           ...p, [editing.id]: { ...(p[editing.id] || {}), [bx.key]: Number(e.target.value) },
                         }))} />
            ))}
            <FormControlLabel
              control={
                <Checkbox checked={Boolean(editing && boxes[editing.id]?.box7_payer_made_direct_sales)}
                          onChange={(e) => editing && setBoxes((p) => ({
                            ...p, [editing.id]: { ...(p[editing.id] || {}), box7_payer_made_direct_sales: e.target.checked },
                          }))} />
              }
              label="Box 7: Direct sales of $5,000+ in consumer products"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirm} onClose={() => setConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transmit 1099-MISC Batch?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will submit {recipientsWithBoxes.length} 1099-MISC forms to the IRS via IRIS.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Test mode validates without transmitting.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => transmit.mutate()} disabled={transmit.isPending}>
            {transmit.isPending ? <CircularProgress size={18} /> : "Transmit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
